/**
 * Invoice Queries
 *
 * React Query hooks for invoice management.
 * Uses centralized mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedInvoiceListViews, persistedInvoiceRecords } from '../mockData/invoices';
import { persistedClientRecords } from '../mockData/clients';
import { generatePaymentLink } from '../paymentLinkService';
import {
  finalizeStripeInvoice,
  sendStripeInvoice,
  createStripeInvoiceFromPricebook,
  listAllStripeInvoices,
  listStripeInvoices,
  getStripeInvoice,
  type CreateStripeInvoiceFromPricebookInput,
} from '../stripeInvoiceService';
import { getStripeCustomerForClientOrAccount } from '../stripeCustomerService';
import { getRecordById, getRecordsForObject } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { Invoice, InvoiceLineItem } from '../../types/index';
import type { ListView, ListViewData, ListViewRecord } from '../../types/listView';

/**
 * Get all invoices.
 * When Gobodo is on, uses listAllStripeInvoices.
 */
export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        return listAllStripeInvoices();
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedInvoiceRecords.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get single invoice by ID.
 * When Gobodo is on and id looks like Stripe id (in_xxx), uses getStripeInvoice.
 */
export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      if (shouldUseGobodo() && typeof id === 'string' && id.startsWith('in_')) {
        const invoice = await getStripeInvoice(id);
        if (!invoice) throw new Error('Invoice not found');
        return invoice;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const invoices = persistedInvoiceRecords.get();
      const invoice = invoices.find(inv => inv.id === id);
      if (!invoice) throw new Error('Invoice not found');
      return invoice;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get invoices for a specific client.
 * When Gobodo is on: Client → Stripe Customer Account → listStripeInvoices(customerId).
 */
export function useClientInvoices(clientId: string) {
  return useQuery({
    queryKey: ['clientInvoices', clientId],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const numericId = Number(clientId);
        if (isNaN(numericId)) return [];
        const stripeCustomer = await getStripeCustomerForClientOrAccount(numericId);
        if (!stripeCustomer) return [];
        const clientRecord = await getRecordById('Client', clientId);
        const clientName = (clientRecord?.name ?? clientRecord?.label ?? (clientRecord as any)?.Name__c ?? '') as string;
        return listStripeInvoices(stripeCustomer.id, { clientId, clientName });
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allInvoices = persistedInvoiceRecords.get();
      return allInvoices.filter(inv => inv.clientId === clientId);
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get invoices that include a specific product (any line item with this productId).
 * When Gobodo is on, no API for this; returns [].
 */
export function useInvoicesByProduct(productId: string) {
  return useQuery({
    queryKey: ['invoicesByProduct', productId],
    queryFn: async () => {
      if (shouldUseGobodo()) return [];
      await new Promise(resolve => setTimeout(resolve, 300));
      const allInvoices = persistedInvoiceRecords.get();
      return allInvoices.filter(
        inv => inv.lineItems && inv.lineItems.some((item: InvoiceLineItem) => item.productId === productId)
      );
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get invoices for all clients linked to an account.
 * When Gobodo is on: Account → Clients → Stripe Customer Account → listStripeInvoices per client, merge.
 */
export function useInvoicesByAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: ['invoicesByAccount', accountId],
    queryFn: async () => {
      if (shouldUseGobodo() && accountId) {
        const clientRecords = await getRecordsForObject('Client__c');
        const clientsForAccount = clientRecords.filter(
          (c: { id: string; accountId?: string; Account__c?: string }) =>
            (c.accountId ?? c.Account__c) === accountId
        );
        const merged: Invoice[] = [];
        for (const client of clientsForAccount) {
          const cid = String(client.id);
          const numericId = Number(cid);
          if (isNaN(numericId)) continue;
          const stripeCustomer = await getStripeCustomerForClientOrAccount(numericId);
          if (!stripeCustomer) continue;
          const clientName = (client.name ?? client.label ?? (client as any).Name__c ?? '') as string;
          const list = await listStripeInvoices(stripeCustomer.id, { clientId: cid, clientName });
          merged.push(...list);
        }
        return merged;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const clients = persistedClientRecords.get();
      const clientIds = clients
        .filter((c: { id: string; accountId?: string }) => c.accountId === accountId)
        .map((c: { id: string }) => c.id);
      if (clientIds.length === 0) return [];
      const allInvoices = persistedInvoiceRecords.get();
      return allInvoices.filter(inv => clientIds.includes(inv.clientId));
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Generate next invoice number
 */
function generateInvoiceNumber(): string {
  const allInvoices = persistedInvoiceRecords.get();
  const numbers = allInvoices
    .map(inv => {
      const match = inv.invoiceNumber.match(/INV-(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => n > 0);
  
  const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return `INV-${String(nextNumber).padStart(5, '0')}`;
}

/**
 * Calculate invoice totals from line items
 */
function calculateInvoiceTotals(lineItems: InvoiceLineItem[]): { subtotal: number; taxAmount: number; totalAmount: number } {
  const subtotal = lineItems.reduce((sum, item) => {
    let itemTotal = item.unitPrice * item.quantity;
    if (item.discount) {
      // Assume discount is percentage
      itemTotal = itemTotal * (1 - item.discount / 100);
    }
    return sum + itemTotal;
  }, 0);
  
  const taxAmount = 0; // Placeholder - no tax calculation for now
  const totalAmount = subtotal + taxAmount;
  
  return { subtotal, taxAmount, totalAmount };
}

/**
 * Create invoice mutation
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoice: Omit<Invoice, 'id' | 'invoiceNumber' | 'subtotal' | 'taxAmount' | 'totalAmount' | 'createdDate' | 'lastModifiedDate' | 'createdBy'>) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const totals = calculateInvoiceTotals(invoice.lineItems);
      
      const newInvoice: Invoice = {
        ...invoice,
        id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        invoiceNumber: generateInvoiceNumber(),
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        createdDate: new Date().toISOString(),
        lastModifiedDate: new Date().toISOString(),
        createdBy: 'current_user'
      };

      persistedInvoiceRecords.add(newInvoice);
      return newInvoice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
    },
  });
}

/**
 * Update invoice mutation
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Invoice> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const existingInvoice = persistedInvoiceRecords.get().find(inv => inv.id === id);
      if (!existingInvoice) throw new Error('Invoice not found');
      
      // Recalculate totals if line items changed
      const lineItems = updates.lineItems || existingInvoice.lineItems;
      const totals = calculateInvoiceTotals(lineItems);
      
      const updated = persistedInvoiceRecords.update(id, {
        ...updates,
        ...totals,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Invoice not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['clientInvoices', data.clientId] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
    },
  });
}

/**
 * Finalize draft invoice mutation.
 * Calls finalizeStripeInvoice so the invoice gets a payment link (can be shared with customer). Send may be implemented later.
 * API-sourced invoices (id in_xxx): call finalizeStripeInvoice directly and invalidate so the UI refetches.
 */
export function useFinalizeInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const isStripeId = typeof invoiceId === 'string' && invoiceId.startsWith('in_');

      if (shouldUseGobodo() && isStripeId) {
        const result = await finalizeStripeInvoice(invoiceId);
        if (result.failureException) throw new Error(result.failureException);
        return { id: invoiceId, clientId: undefined as string | undefined };
      }

      const invoice = persistedInvoiceRecords.get().find(inv => inv.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');

      let paymentLink = invoice.paymentLink;
      const stripeId = invoice.stripeInvoiceId ?? (invoice.id.startsWith('in_') ? invoice.id : null);
      if (shouldUseGobodo() && stripeId) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const result = await finalizeStripeInvoice(stripeId);
        if (result.failureException) throw new Error(result.failureException);
        const invObj = result.invoice && typeof result.invoice === 'object' ? result.invoice : result.invoice?.invoice;
        const hostUrl = invObj?.host_invoice_url ?? invObj?.hostInvoiceUrl ?? invObj?.invoice_url ?? invObj?.invoiceUrl;
        if (typeof hostUrl === 'string' && hostUrl) paymentLink = hostUrl;
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!paymentLink) paymentLink = generatePaymentLink(invoiceId);
      }

      const updated = persistedInvoiceRecords.update(invoiceId, {
        status: 'sent',
        paymentLink,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Invoice not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['clientInvoices', data.clientId] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
    },
  });
}

/**
 * Send invoice mutation (reserved for future use when email send is implemented).
 * When Gobodo is on, calls sendStripeInvoice API.
 */
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const isStripeId = typeof invoiceId === 'string' && invoiceId.startsWith('in_');

      if (shouldUseGobodo() && isStripeId) {
        const result = await sendStripeInvoice(invoiceId);
        if (result.failureException) throw new Error(result.failureException);
        return { id: invoiceId, clientId: undefined as string | undefined };
      }

      const invoice = persistedInvoiceRecords.get().find(inv => inv.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');

      let paymentLink = invoice.paymentLink;
      const stripeId = invoice.stripeInvoiceId ?? (invoice.id.startsWith('in_') ? invoice.id : null);
      if (shouldUseGobodo() && stripeId) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const result = await sendStripeInvoice(stripeId);
        if (result.failureException) throw new Error(result.failureException);
        const invObj = result.invoice && typeof result.invoice === 'object' ? result.invoice : result.invoice?.invoice;
        const hostUrl = invObj?.host_invoice_url ?? invObj?.hostInvoiceUrl ?? invObj?.invoice_url ?? invObj?.invoiceUrl;
        if (typeof hostUrl === 'string' && hostUrl) paymentLink = hostUrl;
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!paymentLink) paymentLink = generatePaymentLink(invoiceId);
      }

      const updated = persistedInvoiceRecords.update(invoiceId, {
        status: 'sent',
        paymentLink,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Invoice not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['clientInvoices', data.clientId] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
    },
  });
}

/**
 * Mark invoice as paid mutation
 */
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updated = persistedInvoiceRecords.update(invoiceId, {
        status: 'paid',
        paidDate: new Date().toISOString().split('T')[0],
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Invoice not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['clientInvoices', data.clientId] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
    },
  });
}

/**
 * Generate payment link mutation
 */
export function useGeneratePaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const invoice = persistedInvoiceRecords.get().find(inv => inv.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');
      
      const paymentLink = generatePaymentLink(invoiceId);
      
      const updated = persistedInvoiceRecords.update(invoiceId, {
        paymentLink,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Invoice not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Get available list views for invoices
 */
export function useInvoiceListViews() {
  return useQuery({
    queryKey: ['invoiceListViews'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedInvoiceListViews.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get list view data (view configuration + records).
 * When Gobodo is on, sources records from listAllStripeInvoices then applies filters/sorts.
 */
export function useInvoiceListViewData(listViewId: string) {
  return useQuery<ListViewData>({
    queryKey: ['invoiceListViewData', listViewId],
    queryFn: async () => {
      const allViews = persistedInvoiceListViews.get();
      let allRecords: ListViewRecord[];
      if (shouldUseGobodo()) {
        allRecords = (await listAllStripeInvoices()) as ListViewRecord[];
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));
        allRecords = persistedInvoiceRecords.get() as ListViewRecord[];
      }

      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) {
        throw new Error('List view not found');
      }

      // Apply filters if any
      let filteredRecords = [...allRecords];

      if (listView.filters && listView.filters.length > 0) {
        listView.filters.forEach(filter => {
          filteredRecords = filteredRecords.filter(record => {
            const fieldValue = record[filter.fieldApiName];

            switch (filter.operator) {
              case 'equals':
                return fieldValue === filter.value;
              case 'notEquals':
                return fieldValue !== filter.value;
              case 'contains':
                return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith':
                return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default:
                return true;
            }
          });
        });
      }

      // Special filter for overdue invoices
      if (listView.id === 'lv_invoices_overdue') {
        const today = new Date().toISOString().split('T')[0];
        filteredRecords = filteredRecords.filter(record => {
          const invoice = record as Invoice;
          return invoice.status !== 'paid' &&
                 invoice.status !== 'cancelled' &&
                 invoice.dueDate < today;
        });
        if (!shouldUseGobodo()) {
          filteredRecords.forEach(record => {
            const invoice = record as Invoice;
            if (invoice.status !== 'overdue') {
              persistedInvoiceRecords.update(invoice.id, { status: 'overdue' });
            }
          });
        }
      }

      // Special filter for this month
      if (listView.id === 'lv_invoices_this_month') {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        filteredRecords = filteredRecords.filter(record => {
          const invoice = record as Invoice;
          return invoice.issueDate >= firstDay && invoice.issueDate <= lastDay;
        });
      }

      // Apply sorting if specified
      if (listView.sortBy) {
        filteredRecords.sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];

          if (listView.sortBy!.direction === 'asc') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
      }

      return {
        listView,
        records: filteredRecords,
        totalRecords: filteredRecords.length
      };
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!listViewId,
  });
}

/**
 * Get default list view for invoices
 */
export function useDefaultInvoiceListView() {
  return useQuery({
    queryKey: ['defaultInvoiceListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      const allViews = persistedInvoiceListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);
      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update an invoice list view
 */
export function useUpdateInvoiceListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      const updated = persistedInvoiceListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });
      if (!updated) throw new Error('List view not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultInvoiceListView'] });
    },
  });
}

/**
 * Create Stripe invoice from pricebook mutation.
 * When Gobodo is on, calls createStripeInvoiceFromPricebook API.
 * Returns invoiceId and hostInvoiceUrl on success.
 */
export function useCreateStripeInvoiceFromPricebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateStripeInvoiceFromPricebookInput) => {
      if (!shouldUseGobodo()) {
        throw new Error('Create invoice from pricebook requires Gobodo API');
      }
      const result = await createStripeInvoiceFromPricebook(input);
      if (result.failureException) throw new Error(result.failureException);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViews'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceListViewData'] });
    },
  });
}
